import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  METRIC,
  RESPONSE,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  SCORE_TYPE,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Reputation module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 *
 * Note: Reputation is unique in that it:
 *   - Enables `supportScoreType` (SCORE_TYPE URL key present in all modes)
 *   - Has `defaultBase: "FAMILIAR"` (USER, ADRECALL, AWARE, GENPOP are disabled)
 *   - By Segment POINT_IN_TIME has both METRIC/RESPONSE AND PROFILE_GROUP/PROFILE_METRIC/PROFILE_RESPONSE
 *   - By Brand POINT_IN_TIME additionally includes TRENDS_METRIC and TRENDS_RESPONSE
 */

export const reputationModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    module: "REPUTATION",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
    supportBase: true,
    // disableBases: ["USER", "ADRECALL", "AWARE", "GENPOP"] → only FAMILIAR and RQ_GENPOP remain
    defaultBase: "FAMILIAR",
    supportedBases: ["FAMILIAR", "RQ_GENPOP"],
    supportDatePeriod: true,
    // Reputation uniquely exposes score-type selection
    supportScoreType: true,
    supportedScoreType: ["MEAN", "LEGACY", "TOP_BOX", "BOTTOM_BOX", "NET"],
    defaultScoreType: "MEAN",
    // Send scoreType inside metricModules in the unified API payload
    apiSendScoreType: true,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["DONUT", "BAR", "PROFILE"],
    defaultVisualisationType: "DONUT",
    supportStatTesting: true,
    apiMetricKeys: ["reputation", "trust", "vision", "quality", "relevance", "ethics", "culture"],
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              VIEW_ID,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              SCORE_TYPE,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "reputation", label: "QRQ" },
              { value: "trust", label: "Trust" },
              { value: "vision", label: "Vision" },
              { value: "quality", label: "Quality" },
              { value: "relevance", label: "Relevance" },
              { value: "ethics", label: "Ethics" },
              { value: "culture", label: "Culture" },
            ],
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            // Reputation By Segment POINT_IN_TIME has METRIC/RESPONSE AND PROFILE_GROUP keys
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  SCORE_TYPE,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              VISUALIZATION_TYPE,
              SCORE_TYPE,
              VIEW_ID,
            ],
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              SCORE_TYPE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "reputation", label: "QRQ" },
              { value: "trust", label: "Trust" },
              { value: "vision", label: "Vision" },
              { value: "quality", label: "Quality" },
              { value: "relevance", label: "Relevance" },
              { value: "ethics", label: "Ethics" },
              { value: "culture", label: "Culture" },
            ],
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            // Reputation By Brand POINT_IN_TIME additionally includes TRENDS_METRIC and TRENDS_RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  SCORE_TYPE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VISUALIZATION_TYPE,
              SCORE_TYPE,
              VIEW_ID,
            ],
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              SCORE_TYPE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "reputation", label: "QRQ" },
              { value: "trust", label: "Trust" },
              { value: "vision", label: "Vision" },
              { value: "quality", label: "Quality" },
              { value: "relevance", label: "Relevance" },
              { value: "ethics", label: "Ethics" },
              { value: "culture", label: "Culture" },
            ],
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            config: {
              supportedURLKeys: [
                MARKETS,
                BRANDS,
                SEGMENTS,
                FILTERS,
                STAT_TEST,
                BASE,
                MODE,
                PERIOD_TYPE,
                PERIOD_START_DATE,
                PERIOD_END_DATE,
                VISUALIZATION_TYPE,
                AGE_START,
                AGE_END,
                PROFILE_GROUP,
                PROFILE_METRIC,
                PROFILE_RESPONSE,
                SCORE_TYPE,
                VIEW_ID,
              ],
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VISUALIZATION_TYPE,
              SCORE_TYPE,
              VIEW_ID,
            ],
          },
        },
      },
    },
  },
};
